import apiClient from './api';
import config from '../../config/environment';

// Serwis do komunikacji z Twoim backendem (który komunikuje się z Allegro API)
export class AllegroApiService {
  // Pobieranie produktów z Twojego backendu z paginacją
  async getProducts(params = {}) {
    try {
      const {
        page = config.PAGINATION.DEFAULT_PAGE,
        limit = config.PAGINATION.DEFAULT_LIMIT,
        search = '',
        status = '',
        sortBy = 'name',
        sortOrder = 'asc',
        ...otherParams
      } = params;

      const queryParams = {
        page,
        limit: Math.min(limit, config.PAGINATION.MAX_LIMIT),
        ...(search && { search }),
        ...(status && { status }),
        ...(sortBy && { sortBy }),
        ...(sortOrder && { sortOrder }),
        ...otherParams,
      };

      console.log('🔍 Search params being sent:', queryParams);

      const response = await apiClient.get(config.API_ENDPOINTS.PRODUCTS, {
        params: queryParams,
      });
      
      console.log('📦 Response from backend:', response.data);
      
      // Obsługa różnych formatów odpowiedzi
      let products = [];
      if (response.data.products) {
        if (Array.isArray(response.data.products)) {
          products = response.data.products;
        } else if (response.data.products.content && Array.isArray(response.data.products.content)) {
          products = response.data.products.content;
        }
      } else if (response.data.content && Array.isArray(response.data.content)) {
        // Spring Page format
        products = response.data.content;
      } else if (Array.isArray(response.data)) {
        products = response.data;
      }
      
      // Obsługa paginacji dla różnych formatów
      let pagination = response.data.pagination;
      if (!pagination) {
        if (response.data.totalElements !== undefined) {
          // Spring Page format
          pagination = {
            page: response.data.number + 1, // Spring używa 0-based indexing
            limit: response.data.size,
            total: response.data.totalElements,
            totalPages: response.data.totalPages,
          };
        } else {
          // Fallback
          pagination = {
            page,
            limit,
            total: response.data.total || products.length,
            totalPages: Math.ceil((response.data.total || products.length) / limit),
          };
        }
      }

      return {
        products: products,
        pagination: pagination,
      };
    } catch (error) {
      console.error('Błąd podczas pobierania produktów:', error);
      throw new Error('Nie udało się pobrać produktów');
    }
  }

  // Pobieranie wszystkich produktów (bez paginacji) - tylko dla małych zestawów danych
  async getAllProducts() {
    try {
      const response = await apiClient.get(config.API_ENDPOINTS.PRODUCTS, {
        params: { limit: config.PAGINATION.MAX_LIMIT }
      });
      
      // Obsługa różnych formatów odpowiedzi - tak samo jak w getProducts()
      let products = [];
      if (response.data.products) {
        if (Array.isArray(response.data.products)) {
          products = response.data.products;
        } else if (response.data.products.content && Array.isArray(response.data.products.content)) {
          products = response.data.products.content;
        }
      } else if (response.data.content && Array.isArray(response.data.content)) {
        // Spring Page format
        products = response.data.content;
      } else if (Array.isArray(response.data)) {
        products = response.data;
      }
      
      return {
        products: products,
        pagination: {
          total: products.length,
          limit: config.PAGINATION.MAX_LIMIT
        }
      };
    } catch (error) {
      console.error('Błąd podczas pobierania wszystkich produktów:', error);
      throw new Error('Nie udało się pobrać produktów');
    }
  }

  // Dodawanie nowego produktu
  async addProduct(productData) {
    try {
      // Sprawdź rozmiar obrazu jeśli istnieje
      if (productData.image?.data) {
        const imageSizeMB = (productData.image.data.length / (1024 * 1024)).toFixed(2);
        console.log('🔍 Rozmiar obrazu produktu:', imageSizeMB, 'MB');
        
        if (parseFloat(imageSizeMB) > 2) {
          throw new Error(`Obraz jest za duży (${imageSizeMB}MB). Maksymalny rozmiar to 2MB.`);
        }
      }
      
      const response = await apiClient.post(config.API_ENDPOINTS.PRODUCTS, productData);
      return response.data;
    } catch (error) {
      console.error('Błąd podczas dodawania produktu:', error);
      
      if (error.response?.status === 403) {
        throw new Error('Brak uprawnień do dodawania produktów. Sprawdź czy jesteś zalogowany.');
      } else if (error.response?.status === 401) {
        throw new Error('Sesja wygasła. Zaloguj się ponownie.');
      } else if (error.response?.status === 413) {
        throw new Error('Plik jest za duży. Maksymalny rozmiar to 2MB. Spróbuj zmniejszyć rozmiar obrazu.');
      } else {
        throw new Error(`Nie udało się dodać produktu: ${error.response?.data?.message || error.message}`);
      }
    }
  }

  // Aktualizacja produktu
  async updateProduct(productId, productData) {
    try {
      const response = await apiClient.put(`${config.API_ENDPOINTS.PRODUCTS}/${productId}`, productData);
      return response.data;
    } catch (error) {
      console.error('Błąd podczas aktualizacji produktu:', error);
      throw new Error('Nie udało się zaktualizować produktu');
    }
  }

  // Usuwanie produktu
  async deleteProduct(productId) {
    try {
      const response = await apiClient.delete(`${config.API_ENDPOINTS.PRODUCTS}/${productId}`);
      return response.data;
    } catch (error) {
      console.error('Błąd podczas usuwania produktu:', error);
      throw new Error('Nie udało się usunąć produktu');
    }
  }

  // Pobieranie obrazka produktu przez imageId
  async getProductImage(imageId) {
    try {
      const response = await apiClient.get(`${config.API_ENDPOINTS.PRODUCTS}/image/${imageId}`, {
        responseType: 'blob',
      });
      
      return response.data;
    } catch (error) {
      console.error('Błąd podczas pobierania obrazka produktu:', error);
      
      if (error.response?.status === 403) {
        throw new Error('Brak uprawnień do pobierania obrazka produktu. Sprawdź czy jesteś zalogowany.');
      } else if (error.response?.status === 401) {
        throw new Error('Sesja wygasła. Zaloguj się ponownie.');
      } else {
        throw new Error(`Nie udało się pobrać obrazka produktu: ${error.response?.data?.message || error.message}`);
      }
    }
  }

  // Aktualizacja zdjęcia produktu
  async updateProductImage(productId, imageDto) {
    try {
      const token = localStorage.getItem(config.STORAGE_KEYS.ACCESS_TOKEN);
      console.log('Token dostępny:', !!token);
      
      // Sprawdź rozmiar obrazu przed wysłaniem
      const imageSizeMB = (imageDto.data.length / (1024 * 1024)).toFixed(2);
      console.log('🔍 Debug obrazu:', {
        originalSize: imageDto.data.length,
        imageSizeMB: imageSizeMB,
        contentType: imageDto.contentType,
        hasId: !!imageDto.id
      });
      
      console.log('Wysyłanie żądania aktualizacji zdjęcia:', {
        url: `${config.API_ENDPOINTS.PRODUCTS}/${productId}/image`,
        productId: productId,
        imageSize: `${imageSizeMB}MB`,
        contentType: imageDto.contentType,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Sprawdź czy obraz nie jest za duży
      if (parseFloat(imageSizeMB) > 2) {
        throw new Error(`Obraz jest za duży (${imageSizeMB}MB). Maksymalny rozmiar to 2MB.`);
      }
      
      // Przygotuj dane do wysłania
      const requestData = {
        id: imageDto.id,
        data: imageDto.data,
        contentType: imageDto.contentType || 'image/jpeg'
      };
      
      const response = await apiClient.put(`${config.API_ENDPOINTS.PRODUCTS}/${productId}/image`, requestData, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Błąd podczas aktualizacji zdjęcia produktu:', error);
      console.error('Szczegóły błędu:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers
      });
      
      if (error.response?.status === 403) {
        throw new Error('Brak uprawnień do aktualizacji zdjęcia produktu. Sprawdź czy jesteś zalogowany.');
      } else if (error.response?.status === 401) {
        throw new Error('Sesja wygasła. Zaloguj się ponownie.');
      } else if (error.response?.status === 413) {
        throw new Error('Plik jest za duży. Maksymalny rozmiar to 2MB. Spróbuj zmniejszyć rozmiar obrazu.');
      } else {
        throw new Error(`Nie udało się zaktualizować zdjęcia produktu: ${error.response?.data?.message || error.message}`);
      }
    }
  }

  // Aktualizacja stanu magazynowego przez Twój backend
  async updateProductStock(productId, accountId, newStock) {
    try {
      const response = await apiClient.patch(`${config.API_ENDPOINTS.PRODUCTS}/${productId}/stock`, {
        accountId,
        stock: newStock,
      });
      return response.data;
    } catch (error) {
      console.error('Błąd podczas aktualizacji stanu:', error);
      throw new Error('Nie udało się zaktualizować stanu magazynowego');
    }
  }

  // Aktualizacja stanu magazynowego przez externalId
  async updateProductQuantity(externalId, newQuantity) {
    try {
      const response = await apiClient.put(`${config.API_ENDPOINTS.PRODUCTS}/${externalId}/quantity`, null, {
        params: { newQuantity }
      });
      return response.data;
    } catch (error) {
      console.error('Błąd podczas aktualizacji stanu magazynowego:', error);
      throw new Error('Nie udało się zaktualizować stanu magazynowego');
    }
  }

  // Pobieranie kont z Twojego backendu
  async getAccounts() {
    try {
      const response = await apiClient.get('/accounts');
      return response.data;
    } catch (error) {
      console.error('Błąd podczas pobierania kont:', error);
      throw new Error('Nie udało się pobrać kont');
    }
  }

  // Dodawanie nowego konta przez Twój backend
  async addAccount(accountData) {
    try {
      const response = await apiClient.post('/accounts', accountData);
      return response.data;
    } catch (error) {
      console.error('Błąd podczas dodawania konta:', error);
      throw new Error('Nie udało się dodać konta');
    }
  }

  // Aktualizacja konta przez Twój backend
  async updateAccount(accountId, accountData) {
    try {
      const response = await apiClient.put(`/accounts/${accountId}`, accountData);
      return response.data;
    } catch (error) {
      console.error('Błąd podczas aktualizacji konta:', error);
      throw new Error('Nie udało się zaktualizować konta');
    }
  }

  // Usuwanie konta przez Twój backend
  async deleteAccount(accountId) {
    try {
      const response = await apiClient.delete(`/accounts/${accountId}`);
      return response.data;
    } catch (error) {
      console.error('Błąd podczas usuwania konta:', error);
      throw new Error('Nie udało się usunąć konta');
    }
  }

  // Synchronizacja przez Twój backend
  async startSync(accountIds) {
    try {
      const response = await apiClient.post('/sync', {
        accountIds,
      });
      return response.data;
    } catch (error) {
      console.error('Błąd podczas synchronizacji:', error);
      throw new Error('Nie udało się zsynchronizować kont');
    }
  }

  // Pobieranie statusu synchronizacji z Twojego backendu
  async getSyncStatus(syncId) {
    try {
      const response = await apiClient.get(`/sync/${syncId}/status`);
      return response.data;
    } catch (error) {
      console.error('Błąd podczas pobierania statusu synchronizacji:', error);
      throw new Error('Nie udało się pobrać statusu synchronizacji');
    }
  }

  // Anulowanie synchronizacji przez Twój backend
  async cancelSync(syncId) {
    try {
      const response = await apiClient.delete(`/sync/${syncId}`);
      return response.data;
    } catch (error) {
      console.error('Błąd podczas anulowania synchronizacji:', error);
      throw new Error('Nie udało się anulować synchronizacji');
    }
  }

  // Pobieranie historii synchronizacji z Twojego backendu
  async getSyncHistory(limit = 10) {
    try {
      const response = await apiClient.get('/sync/history', {
        params: { limit },
      });
      return response.data;
    } catch (error) {
      console.error('Błąd podczas pobierania historii synchronizacji:', error);
      throw new Error('Nie udało się pobrać historii synchronizacji');
    }
  }

  // Pobieranie statystyk synchronizacji z Twojego backendu
  async getSyncStats() {
    try {
      const response = await apiClient.get('/sync/stats');
      return response.data;
    } catch (error) {
      console.error('Błąd podczas pobierania statystyk synchronizacji:', error);
      throw new Error('Nie udało się pobrać statystyk synchronizacji');
    }
  }

  // Pobieranie statystyk magazynowych z Twojego backendu
  async getInventoryStats() {
    try {
      const response = await apiClient.get('/stats');
      return response.data;
    } catch (error) {
      console.error('Błąd podczas pobierania statystyk:', error);
      throw new Error('Nie udało się pobrać statystyk');
    }
  }

  // Eksport danych przez Twój backend
  async exportData(format = 'csv') {
    try {
      const response = await apiClient.get('/export', {
        params: { format },
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      console.error('Błąd podczas eksportu danych:', error);
      throw new Error('Nie udało się wyeksportować danych');
    }
  }

  // Test połączenia z Twoim backendem
  async testConnection() {
    try {
      const response = await apiClient.get('/health');
      return response.data;
    } catch (error) {
      console.error('Błąd połączenia z backendem:', error);
      throw new Error('Nie udało się połączyć z backendem');
    }
  }

  // Pobieranie sklepów z Twojego backendu
  async getShops() {
    try {
      const response = await apiClient.get(config.API_ENDPOINTS.SHOPS);
      return response.data;
    } catch (error) {
      console.error('Błąd podczas pobierania sklepów:', error);
      throw new Error('Nie udało się pobrać sklepów');
    }
  }

  // Dodawanie nowego sklepu
  async addShop(shopData) {
    try {
      const response = await apiClient.post(config.API_ENDPOINTS.SHOPS, shopData);
      return response.data;
    } catch (error) {
      console.error('Błąd podczas dodawania sklepu:', error);
      throw new Error('Nie udało się dodać sklepu');
    }
  }

  // Aktualizacja sklepu
  async updateShop(shopId, shopData) {
    try {
      const response = await apiClient.put(`${config.API_ENDPOINTS.SHOPS}/${shopId}`, shopData);
      return response.data;
    } catch (error) {
      console.error('Błąd podczas aktualizacji sklepu:', error);
      throw new Error('Nie udało się zaktualizować sklepu');
    }
  }

  // Usuwanie sklepu
  async deleteShop(shopId) {
    try {
      const response = await apiClient.delete(`${config.API_ENDPOINTS.SHOPS}/${shopId}`);
      return response.data;
    } catch (error) {
      console.error('Błąd podczas usuwania sklepu:', error);
      throw new Error('Nie udało się usunąć sklepu');
    }
  }

  // Powiązanie oferty z produktem lokalnym
  async linkOfferToProduct(shopId, offerId, externalId) {
    try {
      const response = await apiClient.put(`${config.API_ENDPOINTS.ALLEGRO}/${shopId}/offers/${offerId}/external`, null, {
        params: { externalId }
      });
      return response.data;
    } catch (error) {
      console.error('Błąd podczas powiązywania oferty z produktem:', error);
      throw new Error('Nie udało się powiązać oferty z produktem');
    }
  }

  // Pobieranie ofert sklepu
  async getShopOffers(shopId, status = 'ACTIVE', limit = 20, offset = 0) {
    try {
      const params = {
        'publication.status': status,
        limit,
        offset,
      };
      
      const response = await apiClient.get(`${config.API_ENDPOINTS.ALLEGRO}/${shopId}/offers`, {
        params
      });
      
      return response.data;
    } catch (error) {
      console.error('Błąd podczas pobierania ofert sklepu:', error);
      throw new Error('Nie udało się pobrać ofert sklepu');
    }
  }

  // Pobieranie ustawień aplikacji
  async getSettings() {
    try {
      const response = await apiClient.get('/settings');
      return response.data;
    } catch (error) {
      console.error('Błąd podczas pobierania ustawień:', error);
      throw new Error('Nie udało się pobrać ustawień');
    }
  }

  // Aktualizacja ustawień aplikacji
  async updateSettings(settingsData) {
    try {
      const response = await apiClient.put('/settings', settingsData);
      return response.data;
    } catch (error) {
      console.error('Błąd podczas aktualizacji ustawień:', error);
      throw new Error('Nie udało się zaktualizować ustawień');
    }
  }

  // Aktywacja produktu w sklepie Allegro
  async activateProductInShop(shopId, externalId) {
    try {
      const response = await apiClient.post(`${config.API_ENDPOINTS.ALLEGRO}/${shopId}/externalId/${externalId}/activate`);
      return response.data;
    } catch (error) {
      console.error('Błąd podczas aktywacji produktu w sklepie:', error);
      throw new Error('Nie udało się aktywować produktu w sklepie');
    }
  }

  // Dezaktywacja produktu w sklepie Allegro
  async deactivateProductInShop(shopId, externalId) {
    try {
      const response = await apiClient.post(`${config.API_ENDPOINTS.ALLEGRO}/${shopId}/externalId/${externalId}/deactivate`);
      return response.data;
    } catch (error) {
      console.error('Błąd podczas dezaktywacji produktu w sklepie:', error);
      throw new Error('Nie udało się dezaktywować produktu w sklepie');
    }
  }

  // Ręczne uruchomienie synchronizacji stanu magazynowego
  async syncInventory() {
    try {
      const response = await apiClient.post('/inventory/sync');
      return response.data;
    } catch (error) {
      console.error('Błąd podczas uruchamiania synchronizacji:', error);
      throw new Error('Nie udało się uruchomić synchronizacji');
    }
  }
}

// Singleton instance
export const allegroApiService = new AllegroApiService(); 